import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Column,
  ColumnOptions as GrecoColumnOptions,
} from "components/column-options";
import { ExpandedColumn } from "../../types/columns";
import { Setting } from "../../types/types";
import { usePaymentPlanPageStore } from "store/PaymentPlanPageStore";
import { columns } from "./columns";
import { useAppStore, useAppStoreState } from "store/AppStore";
import { clientSearch, insurerSearch } from "api/api";
import { getShortInsurerName } from "utils/utils";
import {
  areColumnsEquivalent,
  setColumnsVisibility,
} from "utils/columnOptionsState";
import { paymentPlanColumnGroupings } from "./columnGroupings";

/** Columns the grid always renders but the column options panel does not list. */
const isListed = (column: ExpandedColumn) => column.key !== "select";

const moveColumn = (
  columns: ExpandedColumn[],
  fromKey: string,
  toKey: string
) => {
  const from = columns.findIndex((c) => c.key === fromKey);
  const to = columns.findIndex((c) => c.key === toKey);
  if (from === -1 || to === -1) return columns;
  const clone = [...columns];
  clone.splice(to, 0, clone.splice(from, 1)[0]);
  return clone;
};

export const ColumnOptions = ({
  isOpen,
  closePanel,
}: {
  isOpen: boolean;
  closePanel: () => void;
}) => {
  const appStore = useAppStore();
  const [{ taxonomyResponse }, { setAppStoreValue }] = appStore;
  const { t } = useTranslation();

  const [
    {
      cols,
      tempColumns,
      settingsLoadStatus,
      selectedView,
      selectedViewColumns,
      columnOptionsSettings,
    },
    {
      setPaymentPlanStoreValue,
      setPaymentPlanStoreValues,
      deleteSetting,
      updateSetting,
      createSetting,
    },
  ] = usePaymentPlanPageStore();
  const { taxonomyUad } = useAppStoreState();

  const applicationCodeId = taxonomyUad?.ApplicationCode?.find(
    (el) => el.code === "Application.PCT"
  ).id;

  const appSettingCodeId = taxonomyUad?.ApplicationSetting?.find(
    (el) => el.code === "AppSetting.ColumnOptions"
  ).id;
  const areSettingsLoading = settingsLoadStatus === "loading";
  const areSettingsLoaded = settingsLoadStatus !== "loading";

  const isViewLoading = areSettingsLoading;

  const transformedColumns = useMemo(() => {
    const transformedColumns = tempColumns?.map((c) => ({
      ...c,
      label: t(c.labelKey),
      isPresentInColumnOptions: isListed(c),
    }));
    const fixedColumn = transformedColumns?.filter((column) => column.isFixed);

    const unfixedTransformedColumns = transformedColumns?.filter(
      (column) => !column.isFixed
    );

    return fixedColumn?.concat(unfixedTransformedColumns);
  }, [tempColumns, t]);

  const groupings = useMemo(() => paymentPlanColumnGroupings(t, columns), [t]);

  const hasUnsavedChanges =
    !!selectedView &&
    !!selectedViewColumns &&
    !areColumnsEquivalent(tempColumns, selectedViewColumns, isListed);

  if (!isOpen) return null;

  const setTempColumns = (newColumns: ExpandedColumn[]) =>
    setPaymentPlanStoreValue("tempColumns", newColumns);

  // Close and Escape throw away everything that was not applied.
  const discardAndClose = () => {
    setTempColumns(cols);
    closePanel();
  };

  const listedKeys = tempColumns.filter(isListed).map((c) => c.key);

  function updateClientTaxonomies(values: number[]) {
    if (values.length === 0) return;
    clientSearch({
      search: "",
      top: values.length,
      skip: 0,
      partnerType: [900000000003140, 900000000003141],
      filter: `partnerID eq '${values.join("' or partnerID eq '")}'`,
    }).then((res: any) => {
      if (res.isAxiosError) {
        return;
      }
      const options: { document: any }[] = res.data.results;

      let clientTaxonomy = taxonomyResponse["client"];
      const newEntries = options.map((o: { document: any }) => ({
        code: o.document.partnerName || "",
        id: Number(o.document.partnerID),
      }));

      // Add new entries to clientTaxonomy, ignore if they already exist
      const newClientTaxonomy = newEntries.filter(
        (e) => !clientTaxonomy.find((c) => c.id === e.id)
      );
      // Append new entries to clientTaxonomy
      clientTaxonomy = [...clientTaxonomy, ...newClientTaxonomy];

      setAppStoreValue("taxonomyResponse", {
        ...taxonomyResponse,
        client: clientTaxonomy,
      });
    });
  }

  function updateInsurerTaxonomies(values: number[]) {
    if (values.length === 0) return;
    insurerSearch({
      search: "",
      top: values.length,
      skip: 0,
      partnerType: [900000000003142],
      filter: `partnerID eq '${values.join("' or partnerID eq '")}'`,
    }).then((res: any) => {
      if (res.isAxiosError) {
        return;
      }
      const options: { document: any }[] = res.data.results;

      let insurerTaxonomy = taxonomyResponse["insurer"];
      const newEntries = options.map((o: { document: any }) => {
        let name = o.document.partnerName;
        const shortName = getShortInsurerName(o.document.partnerID + "");
        if (shortName !== "") name = shortName!;
        return {
          code: name || "",
          id: Number(o.document.partnerID),
        };
      });

      // Add new entries to insurerTaxonomy, ignore if they already exist
      const newInsurerTaxonomy = newEntries.filter(
        (e) => !insurerTaxonomy.find((c) => c.id === e.id)
      );
      // Append new entries to insurerTaxonomy
      insurerTaxonomy = [...insurerTaxonomy, ...newInsurerTaxonomy];

      setAppStoreValue("taxonomyResponse", {
        ...taxonomyResponse,
        insurer: insurerTaxonomy,
      });
    });
  }

  return (
    <GrecoColumnOptions
      id="payment-plans-column-options"
      t={t}
      columns={transformedColumns as Column[]}
      groupings={groupings}
      hasUnsavedChanges={hasUnsavedChanges}
      isReady={areSettingsLoaded}
      isViewLoading={isViewLoading}
      closePanel={discardAndClose}
      onChangeView={(id) => {
        if (id === null) {
          // "Default" brings back the default columns and filters.
          setPaymentPlanStoreValues({
            selectedViewId: null,
            cols: columns,
            tempColumns: columns,
          });
          return;
        }
        setPaymentPlanStoreValue("selectedViewId", id);
      }}
      onClickApply={() => {
        let values: any = {
          cols: tempColumns,
        };
        const clientColumn = tempColumns.find((c) => c.key === "client") as any;
        if (clientColumn?.filter?.value?.length > 0) {
          values = {
            ...values,
            searchClientsSelectedItem: null,
          };
        }

        const insurerColumn = tempColumns.find(
          (c) => c.key === "insurerName"
        ) as any;
        if (insurerColumn?.filter?.value?.length > 0) {
          values = {
            ...values,
            searchInsurersSelectedItem: null,
          };
        }

        setPaymentPlanStoreValues(values);
        closePanel();
      }}
      onClickClearAllFilters={() => {
        setTempColumns(
          tempColumns.map((c) => ({
            ...c,
            filter: columns.find((dc) => dc.key === c.key)?.filter,
          }))
        );
      }}
      onClickDeleteView={() => {
        deleteSetting(selectedView.userAppSettingId);
        setPaymentPlanStoreValue("selectedViewId", null);
      }}
      onClickReset={() => {
        setTempColumns(selectedViewColumns ?? columns);
      }}
      onClickSaveView={() => {
        updateSetting({
          ...selectedView,
          userAppSettingValue: JSON.stringify(tempColumns),
        });
      }}
      onCreateView={(data) => {
        const newSetting = {
          applicationCodeId: applicationCodeId,
          appSettingCodeId: appSettingCodeId,
          userAppSettingValue: JSON.stringify(tempColumns),
          ...data,
        } as Setting;
        createSetting(newSetting);
        closePanel();
      }}
      onUpdateView={(data) => {
        updateSetting({
          ...selectedView,
          ...data,
        });
        closePanel();
      }}
      onDeselectAll={() => {
        setTempColumns(
          setColumnsVisibility(tempColumns, listedKeys, false, isListed)
        );
      }}
      onSelectAll={() => {
        setTempColumns(
          setColumnsVisibility(tempColumns, listedKeys, true, isListed)
        );
      }}
      onReorderColumn={(src, dest) => {
        // The indexes refer to the columns passed to the panel (fixed ones first).
        const fromKey = transformedColumns[src]?.key;
        const toKey = transformedColumns[dest]?.key;
        if (!fromKey || !toKey) return;
        setTempColumns(moveColumn(tempColumns, fromKey, toKey));
      }}
      onToggleColumnVisibility={(columnKey) => {
        const column = tempColumns.find((c) => c.key === columnKey);
        if (!column) return;
        setTempColumns(
          setColumnsVisibility(
            tempColumns,
            [columnKey],
            !column.isVisible,
            isListed
          )
        );
      }}
      onSetColumnsVisibility={(columnKeys, visible) => {
        setTempColumns(
          setColumnsVisibility(tempColumns, columnKeys, visible, isListed)
        );
      }}
      selectedView={selectedView}
      setFilter={(columnKey, filter) => {
        if ((filter as any).taxonomyKey === "client") {
          updateClientTaxonomies((filter as any).value);
        }
        if ((filter as any).taxonomyKey === "insurer") {
          updateInsurerTaxonomies((filter as any).value);
        }
        setTempColumns(
          tempColumns.map((c) => (c.key === columnKey ? { ...c, filter } : c))
        );
      }}
      taxonomy={taxonomyResponse as any}
      views={columnOptionsSettings}
    />
  );
};
